import { Component, OnDestroy } from '@angular/core';
import Highcharts from 'highcharts';
import { CacheService, ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { InputDateFormatPipe } from 'src/app/modules/core/pipes/input-date-format.pipe';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { MachineContextService } from 'src/app/modules/core/services/parameter/machine-context/machine-context.service';
import { StateSidebarService } from 'src/app/modules/sidebar/services/state-sidebar.service';
import { CommunicationService } from 'src/app/services/communication';
import { IResultLoopsByModelTruck } from '../../../interfaces/production-model-truck';
import { DashboardWidgetChartComponent } from '../../dashboard-widget-chart/dashboard-widget-chart.component';
import { getUniqueValues } from 'projects/common/src/app/services/utils/utilities';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { IShift } from 'src/app/modules/core/interfaces';

@Component({
  selector: 'app-production-by-model-truck-dump-point',
  templateUrl: './production-by-model-truck-dump-point.component.html',
  styleUrls: ['./production-by-model-truck-dump-point.component.scss'],
})
export class ProductionByModelTruckDumpPointComponent extends DashboardWidgetChartComponent implements OnDestroy {
  public productionModelTruckDump: IResultLoopsByModelTruck[];
  public displayMessage: boolean;
  constructor(
    protected progressBarService: BooleanStateService,
    protected communication: CommunicationService,
    protected parameterService: ParameterHandlerService,
    protected sidebarService: StateSidebarService,
    protected cacheService: CacheService,
    protected machineContext: MachineContextService,
    protected languageService: LanguageService,
    private formatDatePipe: InputDateFormatPipe,
    protected screenViewService: ScreenViewService,
  ) {
    super(progressBarService, communication, parameterService, sidebarService, cacheService, machineContext, screenViewService);
    this.initRequesParams();
  }

  /**
   * Init request Params
   */
  public initRequesParams(): void {
    this.params = {
      hasShift: true,
      hasCompany: true,
      hasDate: true,
    };
  }

  /**
   * drawView Chart
   */
  public drawView(chartOptions?: IResultLoopsByModelTruck[]): void {
    this.hasData = !!chartOptions.length;

    const machineType = this.parameterService.parameters.item('machine-context').value || 'no disponible';
    const shift = this.parameterService.parameters.item('shift').value as IShift;
    const user = this.parameterService.parameters.item('user-data').value;

    if (chartOptions.length) {
      this.displayMessage = true;
      this.productionModelTruckDump = chartOptions;

      const op = this.productionModelTruckDump.map((m) => {
        return { op: m.op };
      });

      const model = this.productionModelTruckDump.map((m) => {
        return { modelTruck: m.modelTruck, colour: m.colour, tons: m.tons, op: m.op };
      });

      const categoriesDump = getUniqueValues(op, 'op').map((f) => f.op);
      const modelTrucks = getUniqueValues(model, 'modelTruck').map((m) => m.modelTruck);

      const categories = categoriesDump;
      const modelTruck = modelTrucks;
      const inputDate = String(this.parameterService.parameters.item('date').value);

      const series = [];

      for (const truck of modelTruck) {
        const trucksByModel = this.productionModelTruckDump.filter((f) => f.modelTruck === truck);
        const map = trucksByModel.reduce((prev, next) => {
          if (next.op in prev) {
            prev[next.op].tons += next.tons;
          } else {
            prev[next.op] = next;
          }
          return prev;
        }, {});

        const tonsResult = Object.keys(map).map((id) => map[id]);

        const color = `${this.productionModelTruckDump.find((f) => f.modelTruck === truck).colour}80`;
        const serie = {
          name: truck,
          type: 'column',
          data: tonsResult.filter((f) => f.op).map((m) => m.tons),
          color,
          borderWidth: 2,
          borderColor: color.slice(0, -2),
          states: {
            hover: {
              color,
            },
          },
        };
        series.push(serie);
      }

      const configChart = {
        chart: {
          type: 'column',
          plotBorderWidth: null,
          plotShadow: false,
          backgroundColor: '#F8F8F8',
          style: {
            fontFamily: 'Open Sans',
          },
        },
        subtitle: {
          text: `${this.languageService.message('company')}: ${user.company.name},
                ${this.languageService.message('device')}: ${this.languageService.message(machineType.name)},
                ${this.languageService.message('date')}: ${this.formatDatePipe.transform(inputDate)},
                ${this.languageService.message('shift_')}: ${shift.name}`,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: '',
          style: {
            fontSize: 12,
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#433E3E',
          },
        },
        xAxis: {
          labels: {
            style: {
              color: '#707070',
            },
          },
          categories,
          title: {
            text: this.languageService.message('points-dump').toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
        },
        yAxis: {
          labels: {
            style: {
              color: '#707070',
            },
          },
          title: {
            text: this.languageService.message('tons').toUpperCase(),
            style: {
              fontSize: '12px',
              color: '#707070',
              fontWeight: '600',
            },
          },
        },
        tooltip: {
          backgroundColor: 'rgb(51, 51, 51, 1)',
          borderColor: '#333333',
          borderRadius: 2,
          style: {
            color: '#FFFFFF',
          },
          useHTML: true,
          formatter(): string {
            let divSymbol = '';
            divSymbol = `<div style="background-color: ${this.color};
                              width: 30px;
                              height: 12px;
                              border: 1px solid ${this.color};
                              float: left;
                              margin-top: 2px">
                          </div>`;
            return `<div style="width: 180px;
                                  font-size: 10px;
                                  color: #FFFFFF;">
                        <strong style="font-weight: 700;"></strong> <br>
                        <span style="font-weight: 400;">${this.x}</span> <br>
                        ${divSymbol} &nbsp;
                        <span style="font-weight: 400;">Toneladas: ${this.y.toFixed(1)}</span>
                      </div>`;
          },
        },
        legend: {
          squareSymbol: false,
          align: 'center',
          verticalAlign: 'top',
          symbolHeight: 0.001,
          symbolWidth: 0.001,
          symbolRadius: 0.001,
          margin: 30,
          reversed: true,
          itemStyle: {
            fontSize: 12,
            fontWeight: '600',
            fontFamily: 'Open Sans',
            color: '#433E3E',
          },
          itemCheckboxStyle: {
            color: '#ffffff',
          },
          useHTML: true,
          labelFormatter(): string {
            return `
              <div style=" height: 20px; display: flex;" (click)="null">
                <svg width="36" height="18" style=" float: left;">
                  <rect width="30" height="12" x="2" y="2"
                  style="fill: ${this.color}; stroke-width: 2px !important;
                  stroke: ${this.color.slice(0, -2)} !important;"/>
                </svg>

                <span style="font-size: 12px; font-family: Open Sans; font-weight: 500;
                color: #433E3E; float: left;">${this.name}</span>
              </div>`;
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
            events: {
              legendItemClick(): boolean {
                return false;
              },
            },
            maxPointWidth: 25,
            dataLabels: {
              enabled: true,
              allowOverlap: false,
              verticalAlign() {
                return this.y === 0 ? 'bottom' : 'top';
              },
              style: {
                color: '#707070',
                textOutline: 'none',
                fontWeight: '600',
                fontSize: '11px',
                fontFamily: 'Open Sans',
              },
              y: -20,
              formatter(): string {
                return this.point.y.toFixed(1);
              },
            },
          },
          bar: {
            dataLabels: {
              enabled: true,
              formatter(): number | string {
                return this.y !== 0 ? this.y : '';
              },
            },
          },
        },
        series,
      };

      this.chart = Highcharts.chart(this.chartTarget.nativeElement, configChart as any, (chart) => {
        if (!this.hasData) {
          const newX = chart.plotWidth / 3 + chart.plotLeft;
          const newY = chart.plotHeight / 2 + chart.plotTop;
          chart.renderer
            .text('La solicitud no arrojó resultados', newX, newY)
            .css({
              color: '#4572A7',
              fontSize: '24px',
            })
            .add();
        }
      });
      window.dispatchEvent(new Event('resize'));
    } else {
      this.displayMessage = false;
    }
  }

  /**
   * On destroy component
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
}
